<template>
    <div id="main-sidebar" class="h-full">
        <vx-scroll-area>
            <div v-for="section in Object.keys(menuSections)" :key="section">
                <div class="px-6 flex flex-col">
                    <h5 v-if="section" class="subheader">{{ section }}</h5>
                    <vs-spacer v-if="!section" class="mt-4" />
                    <router-link
                        v-for="(routeInfo, index) in menuSections[section]"
                        :key="index"
                        exact
                        :to="routeInfo.route"
                        tag="span"
                        class="flex items-center justify-between mb-4 cursor-pointer p-1 link"
                        :class="{
                            'bg-primary text-dark-grey-2': checkIsActive(routeInfo.route)
                        }"
                    >
                        <div class="flex items-center">
                            <vx-basic-icon
                                :use-mdi="routeInfo.useMdi"
                                :outlined="routeInfo.outlined"
                                :icon="routeInfo.icon"
                                :svgClasses="[
                                    { 'stroke-current': checkIsActive(routeInfo.route) },
                                    'h-6 w-6'
                                ]"
                            />
                            <span class="text-lg ml-3">{{ routeInfo.label }} </span>
                        </div>
                        <slot name="suffix" :routeInfo="routeInfo">
                            <span v-if="routeInfo.count != null" class="text-lg ml-3">
                                ({{ routeInfo.count }})
                            </span>
                        </slot>
                    </router-link>
                </div>
                <vs-divider class="m-0" v-if="section != Object.keys(menuSections).last()" />
            </div>
        </vx-scroll-area>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { mapParams } from '@/plugins/mappers'

/*
If the menuRoutes is an object, the keys represent groupings of related sub-routes.
Otherwise an array is just a list of routes.

example values:
{
    "Group Heading": [
        "crm-quote-details",
        "crm-opportunities",
        {
            name: "crm-lead-details",
            params: { lead_id: 23 }
        }
    ]
}

OR

[
    // this is the object returned in the scoped slot if required
    {
        label: "Leads",
        icon: "StarIcon",
        route: "crm-leads", // this can be either a string (route name), route object (with params, etc) or an actual route (from getRoute() function)
        count: 24 // this is optional, should be null/undefined if not intended for display
    }
]
*/

export default {
    name: 'vx-main-sidebar',
    props: {
        menuRoutes: {
            type: [Object, Array],
            default: () => {}
        }
    },
    data() {
        return {}
    },
    computed: {
        ...mapGetters({
            breadcrumb: 'router/breadcrumb',
            getRoute: 'router/getByName'
        }),
        ...mapParams({
            statusFilter: 'filter'
        }),
        menuItems() {
            if (Array.isArray(this.menuRoutes)) {
                return {
                    '': this.menuRoutes
                }
            }

            return this.menuRoutes
        },
        checkIsActive() {
            return (route) => this.breadcrumb.any((bc) => bc.name == route.name)
        },
        menuSections() {
            const localMenu = {}
            for (const section in this.menuItems) {
                const routes = this.menuItems[section]
                const processedRoutes = routes.map((item) => {
                    if (!item) {
                        return null
                    }
                    const itemType = typeof item
                    var routeName = null
                    var navigation = null
                    var menuItem = {
                        label: null,
                        icon: null,
                        route: null,
                        count: null,
                        useMdi: false,
                        outlined: false
                    }

                    // A string will mean it's a route name
                    if (itemType === 'string') {
                        routeName = item
                        navigation = {
                            name: routeName
                        }
                    } else if (itemType !== 'object') {
                        throw 'Route must be either the name, item data or route object'
                    }

                    // This should only trigger if the provided "menuItem" is a custom object
                    // which would be the case if we wanted to override things like label and icon
                    if (item.route) {
                        menuItem = item
                        var innerRouteType = typeof menuItem.route
                        if (innerRouteType === 'string') {
                            routeName = item.route
                            navigation = {
                                name: routeName
                            }
                        } else if (innerRouteType === 'object') {
                            routeName = item.route.name
                            navigation = item.route
                        }
                    }

                    if (item.meta) {
                        routeName = item.name
                        navigation = item
                    }

                    // check to make sure the route is accessible with current authorization
                    var route = this.getRoute(routeName)
                    if (!route) {
                        return null
                    }

                    menuItem.icon = menuItem.icon || route.meta.icon
                    menuItem.label = menuItem.label || route.meta.title
                    menuItem.route = {
                        ...route,
                        ...navigation
                    }

                    return menuItem
                })

                localMenu[section] = processedRoutes.where((r) => !!r)
            }

            return localMenu
        }
    },
    methods: {}
}
</script>

<style scoped lang="scss">
@import '@/assets/scss/vuexy/_variables.scss';

.link {
    transition: all 0.25s ease;
    &:hover {
        margin-left: 5px;
        background-color: $grey-wash;
    }
}

.subheader {
    padding: 1.5rem 0;
    position: sticky;
    top: 0;
    background-color: white;
    z-index: 50;
}
.theme-dark {
    .subheader {
        background-color: $theme-dark-bg;
    }
    .link:hover {
        background-color: $dark-grey;
    }
}
</style>
